/**
 * Single source of truth for domain, brand and head-string normalization.
 * Imported by `Seo`, `schema.js` and `seoRoutes.js`; the value of `SITE_URL` is
 * re-exposed on `window.__SEO_SITE_URL__` (see `src/index.js`) so the Node build
 * scripts can read it without importing this ESM module.
 */

export const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://nestly.studio").replace(/\/+$/, "");
export const SITE_NAME = "Nestly";
export const SITE_LOCALE = "it_IT";
export const SITE_LANGUAGE = "it-IT";

/** Editorial hero, cropped to the 1200x630 ratio social crawlers expect. */
export const DEFAULT_OG_IMAGE =
  "https://images.unsplash.com/photo-1583329550487-0fa300a4cd1a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&h=630&fit=crop&auto=format";
export const OG_IMAGE_WIDTH = 1200;
export const OG_IMAGE_HEIGHT = 630;

export const TITLE_MAX = 60;
export const DESCRIPTION_MAX = 155;

const TITLE_SUFFIX = ` — ${SITE_NAME}`;

/** Photo CDNs that accept resize params in the query string. */
const RESIZABLE_IMAGE_HOSTS = new Set(["images.unsplash.com", "images.pexels.com"]);

/**
 * Trailing brand lockups already baked into the seed copy
 * ("… | Nestly", "… — Guida Nestly"), so a suffix is never applied twice.
 */
const BRAND_TAIL = new RegExp(`^(.+?)\\s*[—–|·]\\s*[^—–|·]*\\b${SITE_NAME}\\b\\s*$`, "i");

const MONTHS_IT = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

function collapse(value) {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

/** Cuts on the last word boundary before `max` and drops dangling punctuation. */
function truncateAtWord(text, max) {
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(" ");
  const cut = lastSpace > max / 2 ? slice.slice(0, lastSpace) : slice;
  return cut.replace(/[\s,;:.·–—-]+$/, "");
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

/**
 * Turns a relative path into an absolute URL on the canonical origin.
 * @param {string} [path] Path with leading slash; root is `/`.
 * @returns {string} Absolute URL, trailing slash only on the root.
 */
export function absoluteUrl(path = "/") {
  const raw = collapse(path) || "/";
  const withSlash = raw.startsWith("/") ? raw : `/${raw}`;
  const normalized = withSlash.replace(/\/{2,}/g, "/");
  if (normalized === "/") return `${SITE_URL}/`;
  return `${SITE_URL}${normalized.replace(/\/+$/, "")}`;
}

/**
 * Normalizes an image reference into an absolute 1200x630 social image.
 * @param {string} [src] Absolute URL, CDN URL or site-relative path.
 * @returns {string} Absolute image URL, falling back to {@link DEFAULT_OG_IMAGE}.
 */
export function ogImageUrl(src) {
  if (!src || typeof src !== "string") return DEFAULT_OG_IMAGE;
  let url;
  try {
    url = new URL(src.trim(), `${SITE_URL}/`);
  } catch {
    return DEFAULT_OG_IMAGE;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return DEFAULT_OG_IMAGE;
  if (RESIZABLE_IMAGE_HOSTS.has(url.hostname)) {
    url.searchParams.set("w", String(OG_IMAGE_WIDTH));
    url.searchParams.set("h", String(OG_IMAGE_HEIGHT));
    url.searchParams.set("fit", "crop");
    url.searchParams.set("auto", "format");
  }
  return url.toString();
}

/**
 * Appends the brand lockup and keeps the result within {@link TITLE_MAX}.
 * Only the page-specific part is ever truncated.
 * @param {string} title Page title without brand suffix.
 * @returns {string}
 */
export function formatTitle(title) {
  const raw = collapse(title);
  if (!raw) return SITE_NAME;
  const base = collapse(raw.replace(BRAND_TAIL, "$1")) || raw;
  const room = TITLE_MAX - TITLE_SUFFIX.length;
  return `${truncateAtWord(base, room)}${TITLE_SUFFIX}`;
}

/**
 * Keeps a meta description within {@link DESCRIPTION_MAX} without cutting mid-word.
 * @param {string} text
 * @returns {string}
 */
export function clampDescription(text) {
  const raw = collapse(text);
  if (raw.length <= DESCRIPTION_MAX) return raw;
  return `${truncateAtWord(raw, DESCRIPTION_MAX - 1)}…`;
}

/**
 * Parses a real publication date into `YYYY-MM-DD`.
 * Understands ISO strings, `Date` objects and Italian long dates ("12 dicembre 2025").
 * @param {string|Date|null|undefined} value
 * @returns {string|null} `null` when no real date can be parsed — never a fallback date.
 */
export function toIsoDate(value) {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`;
  }

  if (typeof value !== "string") return null;
  const raw = collapse(value);
  if (!raw) return null;

  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;

  const italian = raw.match(/^(\d{1,2})\s+([A-Za-zÀ-ÿ]+)\s+(\d{4})$/);
  if (italian) {
    const monthIndex = MONTHS_IT.indexOf(italian[2].toLowerCase());
    if (monthIndex >= 0) return `${italian[3]}-${pad2(monthIndex + 1)}-${pad2(Number(italian[1]))}`;
  }

  return null;
}
